import {computed, Injectable, signal} from '@angular/core';
import {SessionService, SpaceSettingsService} from '@q9elements/ui-kit/common';
import {FilterDescriptors, GridQuery, GridStore} from '@q9elements/ui-kit/components';
import {assign, find, map} from 'lodash';

import {DEFAULT_VIEW_ID} from '../models/constants';
import {GridView} from '../models/interfaces';

@Injectable()
export class GridViewsService {
  private viewsIds = computed(() => map(this.views(), 'id'));

  public readonly views = signal<GridView[]>([]);
  public readonly selectedViewId = signal<string>('');
  public readonly selectedView = computed<GridView | null>(() => {
    if (this.selectedViewId()) {
      return find(this.views(), {id: this.selectedViewId()})!;
    }

    return null;
  });

  constructor(
    private store: GridStore,
    private query: GridQuery,
    private sessionService: SessionService,
    private spaceSettingsService: SpaceSettingsService
  ) {}

  public addView(view: GridView) {
    this.views.update(views => [...views, view]);
  }

  public cloneView(view: GridView): Partial<GridView> {
    const {name, columns, filters} = view;

    return {columns, filters, name: `CLONE - ${name}`};
  }

  public editView(view: GridView) {
    this.views.update(views => {
      const updatedView = find(views, {id: view?.id});

      if (updatedView) {
        assign(updatedView, view);
      }

      return views;
    });
  }

  public editFilters(id: string, filters: FilterDescriptors) {
    this.views.mutate(views => {
      const view = find(views, {id});

      if (view) {
        view.filters = filters;
      }
    });
  }

  public deleteView(id: string) {
    this.views.update(views => views.filter(view => view.id !== id));
  }

  public storeViews(views: GridView[]) {
    this.views.set([this.defaultGridView, ...views]);
  }

  public selectView(id: string) {
    const viewId = this.viewsIds().includes(id) ? id : DEFAULT_VIEW_ID;

    this.selectedViewId.set(viewId);
    this.spaceSettingsService.updateSettingsByKey(`${this.moduleType}.gridViewId`, viewId);
  }

  private get moduleType() {
    return this.store.storeName;
  }

  private get defaultGridView(): GridView {
    const columns = this.query.getColumns();

    return {
      columns: map(columns, 'field'),
      id: DEFAULT_VIEW_ID,
      name: 'All',
      default: true,
      type: this.moduleType,
      team: this.sessionService.teamId()!
    };
  }
}
